import React, { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Check, X, ChevronRight, Copy } from "lucide-react";
import { useUiMode } from "../hooks/useUiMode";

const TOOL_VERB = {
  query_database: {
    decided:   "Preparing query",
    started:   "Querying database",
    completed: "Database query",
    failed:    "Query failed",
  },
  render_chart: {
    decided:   "Preparing chart",
    started:   "Drawing chart",
    completed: "Chart drawn",
    failed:    "Chart failed",
  },
  get_table_schema: {
    decided:   "Looking up schema",
    started:   "Looking up table",
    completed: "Schema lookup",
    failed:    "Lookup failed",
  },
  ask_clarifying_question: {
    decided:   "Preparing question",
    started:   "Asking",
    completed: "Asked",
    failed:    "Question failed",
  },
};

// Bug 22: human-readable display names for the LLM-facing tool function names. Users should
// never see raw snake_case names like `aggregate_answers_by_question` in a pill. Tools with
// status-specific phrasing in TOOL_VERB keep that richer phrasing; everything else uses this
// status-agnostic label (the icon already conveys running/done/failed). Unknown/new tools
// degrade to a title-cased, space-separated version of the raw name.
const TOOL_DISPLAY_NAMES = {
  get_execution_full: "Reading record",
  find_executions_by_filter: "Searching records",
  find_question_by_intent: "Finding question",
  aggregate_answers_by_question: "Counting answers",
  get_tasks_spawned_by_execution: "Looking up tasks",
  get_executions_spawned_by_execution: "Looking up linked records",
  get_execution_history: "Reading history",
  state_assumption: "Noting assumption",
  ask_clarifying_question: "Asking for clarification",
  query_database: "Custom query",
  lookup_resolver: "Resolving lookup",
  render_chart: "Building chart",
  share_progress: "Update",
  web_search: "Searching the web",
  web_fetch: "Reading web page",
};

function toolDisplayName(name) {
  if (!name) return "Working";
  if (TOOL_DISPLAY_NAMES[name]) return TOOL_DISPLAY_NAMES[name];
  return String(name).split("_").map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(" ");
}

// Plain-English fallback summary used when the dev-mode summary is too
// technical for end users (e.g. "30 rows in 2407ms" → "Got the data").
// Keyed by tool + status; only entries we want to override are listed.
const PROD_SUMMARY = {
  query_database: {
    completed: "Got the data",
    failed:    "We hit an issue running this. Try rephrasing your question.",
  },
  render_chart: {
    completed: "Chart ready",
  },
};

export default function ToolCallPill({ toolCall, count = 1 }) {
  const { uiMode } = useUiMode();
  const isProd = uiMode === "prod";
  const { name, status, summary, error, executedSql, rowCount } = toolCall;
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  // TOOL_VERB carries status-specific phrasing for a few tools; everything else uses the
  // friendly status-agnostic display name (Bug 22) — never the raw snake_case function name.
  const verbMap = TOOL_VERB[name];
  const verb = verbMap ? (verbMap[status] || toolDisplayName(name)) : toolDisplayName(name);
  // Defensive: a non-string or literal "[object Object]" summary means an
  // upstream stringified an envelope it shouldn't have. Suppress it rather
  // than show junk — the error.message line below carries the real text.
  const rawSummary = isProd
    ? (PROD_SUMMARY[name] && PROD_SUMMARY[name][status]) || null
    : summary;
  const visibleSummary =
    typeof rawSummary === "string" && rawSummary !== "[object Object]"
      ? rawSummary
      : null;
  // In prod we deliberately swallow the structured `error` payload's
  // technical message (parse errors, validator violation codes, etc.).
  // The PROD_SUMMARY 'failed' string above stands in as the user-facing
  // message; the icon + verb still show that something failed.
  // In dev, surface error.message with a fallback chain so the user sees
  // a real driver/parse message even if .message is somehow empty.
  const errorText = error && (error.message || error.code) || null;
  const visibleError = isProd ? null : (errorText ? { ...error, message: errorText } : null);

  const titleColor =
    status === "decided"   ? "text-gray-500"
  : status === "started"   ? "text-blue-700"
  : status === "completed" ? "text-gray-700"
  :                          "text-red-700";

  const iconColor =
    status === "decided"   ? "text-gray-400"
  : status === "started"   ? "text-blue-600"
  : status === "completed" ? "text-emerald-500"
  :                          "text-red-500";

  // Show the View-SQL accordion only for query_database calls that have
  // resolved (success or failure) and where the SQL was captured. Decided
  // and started states don't have it yet. Hidden entirely in prod mode.
  const showSqlAccordion =
    !isProd && name === "query_database" && executedSql && (status === "completed" || status === "failed");

  const handleCopy = (e) => {
    e.stopPropagation();
    if (!executedSql) return;
    navigator.clipboard.writeText(executedSql).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {
      // Clipboard write can fail on insecure contexts — fall back silently.
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="bg-white border border-gray-200 rounded-lg overflow-hidden"
    >
      <div className="flex items-start gap-3 px-3.5 py-2.5">
        <div className="mt-0.5 flex-shrink-0">
          {status === "decided" || status === "started" ? (
            <Loader2 className={`w-4 h-4 ${iconColor} animate-spin`} strokeWidth={2.5} />
          ) : status === "completed" ? (
            <Check className={`w-4 h-4 ${iconColor}`} strokeWidth={3} />
          ) : (
            <X className={`w-4 h-4 ${iconColor}`} strokeWidth={3} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-medium ${titleColor} flex items-center gap-1.5`}>
            <span title={name}>{verb}</span>
            {count > 1 && (
              <span className="text-xs font-normal text-gray-400">×{count}</span>
            )}
          </div>
          {visibleSummary && (
            <div className="text-xs text-gray-500 mt-0.5 truncate">
              {visibleSummary}
            </div>
          )}
          {visibleError && visibleError.message && (
            <div className="text-xs text-red-600 mt-1">
              {visibleError.message}
            </div>
          )}
        </div>
      </div>

      {showSqlAccordion && (
        <div className="border-t border-gray-100">
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center gap-2 px-3.5 py-2 text-xs text-gray-500 hover:bg-gray-50 transition-colors"
          >
            <ChevronRight
              className={`w-3.5 h-3.5 transition-transform duration-200 ${expanded ? "rotate-90" : ""}`}
              strokeWidth={2}
            />
            <span>{expanded ? "Hide SQL" : "View SQL"}</span>
            {rowCount !== undefined && (
              <span className="ml-auto text-gray-400">
                {rowCount} {rowCount === 1 ? "row" : "rows"}
              </span>
            )}
          </button>
          {expanded && (
            <div className="px-3.5 pb-3 pt-1">
              <div className="relative">
                <pre className="text-xs bg-gray-900 text-gray-100 rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-all font-mono leading-relaxed max-h-96">
                  {executedSql}
                </pre>
                <button
                  onClick={handleCopy}
                  className="absolute top-2 right-2 px-2 py-1 bg-gray-700 hover:bg-gray-600 text-gray-200 text-xs rounded flex items-center gap-1 transition-colors"
                  title="Copy SQL"
                >
                  <Copy className="w-3 h-3" strokeWidth={2} />
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}
